import type { EmailLocalePreferenceDTO } from './accountTypes.js';
import {
  resolveCustomerAuthLocale,
  type CustomerAuthLocale,
} from '../../shared/http/platformEnvelopeLocale.js';

export const EMAIL_LOCALE_CODES = ['cs', 'en', 'sk'] as const;

export type EmailLocaleCode = (typeof EMAIL_LOCALE_CODES)[number];

export type EmailLocalePreferenceMode = 'default' | 'custom';

export interface EmailLocalePreferenceUiState {
  readonly mode: EmailLocalePreferenceMode;
  readonly customLocale: EmailLocaleCode;
}

/** PUT body for email-locale preference. */
export type EmailLocalePutBody =
  | { mode: 'default'; uiLocale: EmailLocaleCode }
  | { mode: 'custom'; locale: EmailLocaleCode };

export function isEmailLocaleCode(value: string | undefined | null): value is EmailLocaleCode {
  return value === 'cs' || value === 'en' || value === 'sk';
}

/** Map i18n language (e.g. `en-US`, `sk`) to an email locale code. */
export function resolveEmailUiLocale(language: string | undefined): EmailLocaleCode {
  return resolveCustomerAuthLocale(language);
}

/**
 * Normalize stored/API uiLocale or locale to an allowed email code, or null.
 */
export function normalizeEmailUiLocale(
  value: string | null | undefined,
): EmailLocaleCode | null {
  if (value === undefined || value === null) {
    return null;
  }
  return isEmailLocaleCode(value) ? value : null;
}

/**
 * Server-effective Default locale: `normalize(uiLocale) ?? 'cs'`.
 */
export function serverEffectiveEmailDefaultLocale(
  data: EmailLocalePreferenceDTO | undefined,
): EmailLocaleCode {
  if (data === undefined) {
    return 'cs';
  }
  return normalizeEmailUiLocale(data.uiLocale) ?? 'cs';
}

function isDefaultDto(data: EmailLocalePreferenceDTO | undefined): boolean {
  if (data === undefined) {
    return true;
  }
  if (data.mode === 'default' || data.isPlatformDefault) {
    return true;
  }
  if (data.mode === 'custom') {
    return false;
  }
  return data.isPlatformDefault;
}

/**
 * Default is a mode (follow UI), never inferred from locale === uiLocale.
 * Custom is only an explicit fixed locale from the server.
 */
export function emailPreferenceFromDto(
  data: EmailLocalePreferenceDTO | undefined,
  uiLocale: EmailLocaleCode,
): EmailLocalePreferenceUiState {
  if (isDefaultDto(data)) {
    return { mode: 'default', customLocale: uiLocale };
  }
  const stored = data?.locale;
  if (isEmailLocaleCode(stored)) {
    return { mode: 'custom', customLocale: stored };
  }
  return { mode: 'custom', customLocale: uiLocale };
}

export function emailPreferenceToPutBody(
  state: EmailLocalePreferenceUiState,
  uiLocale: EmailLocaleCode,
): EmailLocalePutBody {
  if (state.mode === 'default') {
    return { mode: 'default', uiLocale };
  }
  return { mode: 'custom', locale: state.customLocale };
}

/** Effective single locale the UI highlights for this state. */
export function emailPreferenceEffectiveLocale(
  state: EmailLocalePreferenceUiState,
  uiLocale: EmailLocaleCode,
): EmailLocaleCode {
  return state.mode === 'default' ? uiLocale : state.customLocale;
}

export function emailPreferencePutBodiesEqual(
  left: EmailLocalePutBody,
  right: EmailLocalePutBody,
): boolean {
  if (left.mode !== right.mode) {
    return false;
  }
  if (left.mode === 'default' && right.mode === 'default') {
    return left.uiLocale === right.uiLocale;
  }
  if (left.mode === 'custom' && right.mode === 'custom') {
    return left.locale === right.locale;
  }
  return false;
}

export type { CustomerAuthLocale };
